import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import { apiClient, formatDate, Badge, Button, Spinner } from '@dukene/shared';

interface EmailRecord {
    id: string;
    resendId: string | null;
    from: string;
    to: string;
    subject: string;
    body: string | null;
    html: string | null;
    status: 'SENT' | 'DELIVERED' | 'OPENED' | 'CLICKED' | 'BOUNCED' | 'COMPLAINED' | 'FAILED';
    direction: 'INBOUND' | 'OUTBOUND';
    createdAt: string;
    metadata: any;
}

export default function EmailDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { data: email, isLoading, error, refetch } = useQuery<EmailRecord>({
        queryKey: ['email-detail', id],
        queryFn: async () => {
            return apiClient.get<EmailRecord>(`/emails/history/${id}`);
        },
    });

    const [isSyncing, setIsSyncing] = React.useState(false);

    const handleSync = async () => {
        if (!id) return;
        setIsSyncing(true);
        try {
            await apiClient.post(`/emails/history/${id}/sync`, {});
            await refetch();
        } catch (err) {
            console.error('Sync failed:', err);
        } finally {
            setIsSyncing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center">
                <Spinner className="w-10 h-10 text-black border-2" />
                <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Opening Message...</p>
            </div>
        );
    }

    if (error || !email) {
        return (
            <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm max-w-2xl mx-auto">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2">Message Not Found</h3>
                <p className="text-gray-500 mb-8">This email could not be retrieved. It may have been permanently deleted.</p>
                <Button onClick={() => navigate('/email')} variant="secondary" className="rounded-xl px-8">
                    Back to Inbox
                </Button>
            </div>
        );
    }

    const getStatusBadge = (status: EmailRecord['status']) => {
        switch (status) {
            case 'SENT': return <Badge className="bg-blue-50 text-blue-600 border-blue-100 px-3 py-1 uppercase text-[10px] font-black">Sent</Badge>;
            case 'DELIVERED': return <Badge className="bg-emerald-50 text-emerald-600 border-emerald-100 px-3 py-1 uppercase text-[10px] font-black">Delivered</Badge>;
            case 'OPENED': return <Badge className="bg-violet-50 text-violet-600 border-violet-100 px-3 py-1 uppercase text-[10px] font-black">Opened</Badge>;
            case 'CLICKED': return <Badge className="bg-indigo-50 text-indigo-600 border-indigo-100 px-3 py-1 uppercase text-[10px] font-black">Clicked</Badge>;
            case 'FAILED': return <Badge className="bg-rose-50 text-rose-600 border-rose-100 px-3 py-1 uppercase text-[10px] font-black">Failed</Badge>;
            default: return <Badge className="bg-gray-50 text-gray-600 border-gray-100 px-3 py-1 uppercase text-[10px] font-black">{status}</Badge>;
        }
    };

    const hasNoContent = !email.html && !email.body;

    return (
        <div className="max-w-4xl mx-auto pb-20 px-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-10">
                <button
                    onClick={() => navigate('/email')}
                    className="flex items-center gap-3 text-gray-500 hover:text-black transition-all group"
                >
                    <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center group-hover:bg-gray-50 group-hover:border-black shadow-sm transition-all">
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-black uppercase tracking-widest">Back</span>
                </button>

                <div className="flex items-center gap-3">
                    {hasNoContent && (
                        <Button
                            onClick={handleSync}
                            disabled={isSyncing}
                            variant="secondary"
                            size="sm"
                            className="rounded-full flex items-center gap-2 font-black text-[10px] uppercase px-5 py-2.5 bg-white border-gray-200 hover:border-black transition-all"
                        >
                            {isSyncing ? <Spinner size="sm" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            {isSyncing ? 'Loading Content...' : 'Reload Message'}
                        </Button>
                    )}
                    <Badge className="bg-black text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                        {email.direction === 'OUTBOUND' ? 'Sent' : 'Received'}
                    </Badge>
                </div>
            </div>

            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                {/* Main Card */}
                <div className="bg-white border border-gray-200 rounded-[32px] overflow-hidden shadow-sm">
                    {/* Header Section */}
                    <div className="p-8 md:p-12 border-b border-gray-50 bg-gray-50/10">
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                {getStatusBadge(email.status)}
                                <div className="w-1 h-1 bg-gray-200 rounded-full" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                                    {formatDate(email.createdAt)}
                                </span>
                            </div>
                            <h1 className="text-3xl md:text-5xl font-black text-black leading-tight tracking-tight">
                                {email.subject}
                            </h1>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10 p-6 bg-white rounded-2xl border border-gray-50">
                            <div className="space-y-1">
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Sender</p>
                                <p className="font-bold text-gray-900 break-all">{email.from}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Recipient</p>
                                <p className="font-bold text-gray-900 break-all">{email.to}</p>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-8 md:p-12 bg-white min-h-[400px]">
                        {email.html ? (
                            <div
                                className="prose prose-blue max-w-none text-gray-900 leading-relaxed font-medium email-html-content"
                                dangerouslySetInnerHTML={{ __html: email.html }}
                            />
                        ) : email.body ? (
                            <p className="whitespace-pre-wrap text-gray-800 leading-loose font-medium font-mono text-sm">
                                {email.body}
                            </p>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 text-center space-y-6">
                                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-200 border border-gray-100">
                                    <RefreshCw className="w-6 h-6 animate-spin" />
                                </div>
                                <div className="space-y-2">
                                    <p className="font-black text-gray-900">Fetching Content...</p>
                                    <p className="text-xs text-gray-400 max-w-xs mx-auto">This message content is being securely retrieved from the server.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Status Timeline - Simplified */}
                <div className="p-2">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4 ml-4">Activity History</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {email.metadata?.events?.slice(0, 6).map((event: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
                                <span className="text-[9px] font-black text-black uppercase tracking-wider">
                                    {event.type.replace('email.', '').replace('_', ' ')}
                                </span>
                                <span className="text-[9px] font-bold text-gray-400">
                                    {new Date(event.timestamp).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer Action */}
                <div className="flex justify-center pt-8">
                    <Button
                        onClick={() => navigate('/email/compose')}
                        className="bg-black text-white hover:bg-gray-800 rounded-2xl px-12 py-5 text-lg font-black shadow-xl shadow-black/10 hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center gap-4"
                    >
                        <Mail className="w-5 h-5" />
                        Send Reply
                    </Button>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .email-html-content img { max-width: 100%; height: auto; border-radius: 8px; }
                .email-html-content a { color: #000; text-decoration: underline; font-weight: 800; }
                .email-html-content table { border-collapse: collapse; width: 100%; margin: 16px 0; font-size: 14px; }
                .email-html-content td { padding: 12px; border: 1px solid #f9fafb; }
            `}} />
        </div>
    );
}
